import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation, Location } from 'react-router-dom';
import clsx from 'clsx';
import { IMenuItem } from 'types/sidebar';
import ModalClearCache from 'pages/clear-cache';
import { openModal } from 'services/modal';
import { Role, SETTING_SIDEBAR_ACCOUNTANT, StatusSlidebar } from 'consts/common.const';
import { useSession } from 'hooks/use-session.hook';

const MenuItem = ({
  menuItem,
  statusSidebar,
  isCheckCollapse,
}: {
  menuItem: IMenuItem;
  statusSidebar: number;
  isCheckCollapse: boolean;
}) => {
  const [t] = useTranslation();
  const [isMenuExtended, setIsMenuExtended] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);
  const [isMainActive, setIsMainActive] = useState(false);
  const [isOneOfChildrenActive, setIsOneOfChildrenActive] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSession();

  const toggleMenu = () => {
    setIsMenuExtended(!isMenuExtended);
  };

  const handleMainMenuAction = () => {
    if (menuItem.path === '/clear-cache') {
      const modal = openModal(ModalClearCache);

      modal.afterClosed().subscribe();
    } else if (isExpandable) {
      toggleMenu();

      return;
    } else {
      navigate(menuItem.path || '/');
    }
  };

  const calculateIsActive = useCallback(
    (location: Location) => {
      setIsMainActive(false);
      setIsOneOfChildrenActive(false);

      if (isExpandable && menuItem && menuItem.children) {
        menuItem.children.forEach((item) => {
          if (item.path === location.pathname) {
            setIsOneOfChildrenActive(true);
            setIsMenuExtended(true);
          }
        });
      } else if (location.pathname.split('/').slice(1).includes(menuItem.path.slice(1))) {
        setIsMainActive(true);
      }
    },
    [isExpandable, menuItem]
  );

  useEffect(() => {
    if (menuItem && menuItem.children) {
      menuItem.children.forEach((item) => {
        if (item.path === location.pathname.slice(1)) {
          setIsMenuExtended(true);
        }
      });
    }
  }, [location.pathname, menuItem]);

  useEffect(() => {
    if (location) {
      calculateIsActive(location);
    }
  }, [location, calculateIsActive]);

  useEffect(() => {
    setIsExpandable(Boolean(menuItem && menuItem.children && !!menuItem.children.length));
  }, [menuItem]);
  const isValidPermissionSubnav = (pathname) => {
    const isValidRoleDefaultChat = [Role.ADMIN, Role.ROOT, Role.AM].includes(userInfo?.role);
    const isValidRoleDefaultKpi = [Role.ADMIN, Role.ROOT, Role.AM].includes(userInfo?.role);
    const isValidKpi = pathname.includes('kpi');
    const isValidParagraphChat = pathname.includes('paragraph-chat');

    if (isValidParagraphChat) {
      return isValidRoleDefaultChat || pathname.includes(userInfo?.roleName.toLowerCase());
    } else if (isValidKpi) {
      return isValidRoleDefaultKpi || pathname.includes(userInfo?.roleName.toLowerCase());
    } else {
      return true;
    }
  };

  return (
    <li className={clsx('nav-item', { 'menu-open': isMenuExtended })}>
      <a
        role='link'
        className={clsx('nav-link', { active: isMainActive || isOneOfChildrenActive })}
        onClick={handleMainMenuAction}
      >
        <i className={`nav-icon ${menuItem.icon || 'fas fa-edit'}`} />
        <p
          className={
            statusSidebar === StatusSlidebar.collapse || (isCheckCollapse && statusSidebar === StatusSlidebar.default)
              ? 'nav-text'
              : ''
          }
        >
          {isExpandable &&
          (statusSidebar === StatusSlidebar.collapse ||
            (isCheckCollapse && statusSidebar === StatusSlidebar.default)) ? (
            <div>
              <ul className='nav nav-sidebar' style={{ display: 'none' }}>
                {menuItem.children.map((item, index) => {
                  const isSetHighlight = location.pathname !== 'setting' && location.pathname.includes(`/${item.path}`);

                  return userInfo.role !== Role.ACCOUNTANT
                    ? isValidPermissionSubnav(item.path) && (
                        <li className='nav-item' key={index}>
                          <Link
                            to={`${item.path}`}
                            className={clsx('nav-link', {
                              active: location.pathname === `/${item.path}` || isSetHighlight,
                            })}
                          >
                            <i className={`nav-icon ${item.icon}`} />
                            <p className=''>{t(item.name)}</p>
                          </Link>
                        </li>
                      )
                    : isValidPermissionSubnav(item.path) && SETTING_SIDEBAR_ACCOUNTANT.includes(item.path) && (
                        <li className='nav-item' key={index}>
                          <Link
                            to={`${item.path}`}
                            className={clsx('nav-link', {
                              active: location.pathname === ` /${item.path}` || isSetHighlight,
                            })}
                          >
                            <i className={`nav-icon ${item.icon}`} />
                            <p className=''>{t(item.name)}</p>
                          </Link>
                        </li>
                      );
                })}
              </ul>
            </div>
          ) : (
            <span>{t(menuItem.name)}</span>
          )}
          {isExpandable && statusSidebar !== StatusSlidebar.collapse && !isCheckCollapse ? (
            <i className='right fas fa-angle-left' />
          ) : null}
        </p>
      </a>

      {isExpandable && isMenuExtended && statusSidebar !== StatusSlidebar.collapse && !isCheckCollapse && (
        <ul className='nav nav-sidebar indent'>
          {menuItem.children.map((item, index) => {
            const isSetHighlight =
              !location.pathname.includes('setting') && location.pathname.includes(`/${item.path}`);

            return userInfo.role !== Role.ACCOUNTANT
              ? isValidPermissionSubnav(item.path) && (
                  <li className='nav-item' key={index}>
                    <Link
                      to={`${item.path}`}
                      className={clsx('nav-link', { active: location.pathname === `/${item.path}` || isSetHighlight })}
                    >
                      <i className={`nav-icon ${item.icon}`} />
                      <p className=''>{t(item.name)}</p>
                    </Link>
                  </li>
                )
              : isValidPermissionSubnav(item.path) && SETTING_SIDEBAR_ACCOUNTANT.includes(item.path) && (
                  <li className='nav-item' key={index}>
                    <Link
                      to={`${item.path}`}
                      className={clsx('nav-link', { active: location.pathname === `/${item.path}` || isSetHighlight })}
                    >
                      <i className={`nav-icon ${item.icon}`} />
                      <p className=''>{t(item.name)}</p>
                    </Link>
                  </li>
                );
          })}
        </ul>
      )}
    </li>
  );
};

export default MenuItem;
